import type { ConversationStatus } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@eriko/ui/src/components/ui/dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreVertical } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useOrgConversationDetail } from '../../../hooks/useOrgConversations';
import { resolveConversation, archiveConversation } from '../../../api/conversations';
import { MessageRow } from './MessageRow';
import type { PublicUser } from './profileCache';
import { getProfile } from './profileCache';
import { ReplyInput } from './ReplyInput';
import { ConvArtifactPanel } from './ConvArtifactPanel';
import { useConvArtifactStore } from './convArtifactStore';
import { loadEntityDetailForConv } from './loadEntityDetailForConv';

const STATUS_VARIANT: Record<
  ConversationStatus,
  'secondary' | 'warning' | 'success' | 'outline'
> = {
  OPEN: 'secondary',
  NEEDS_REPLY: 'warning',
  RESOLVED: 'success',
  ARCHIVED: 'outline',
};

interface ConvPaneProps {
  orgId: string;
  conversationId: string;
}

export function ConvPane({ orgId, conversationId }: ConvPaneProps) {
  const { data: detail, isLoading } = useOrgConversationDetail(orgId, conversationId);
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [profiles, setProfiles] = useState<Record<string, PublicUser>>({});

  useEffect(() => {
    const ids = new Set<string>();
    for (const m of detail?.messages ?? []) if (m.senderId) ids.add(m.senderId);
    (async () => {
      const updates: Record<string, PublicUser> = {};
      for (const id of ids) {
        if (profiles[id]) continue;
        const p = await getProfile(id);
        if (p) updates[id] = p;
      }
      if (Object.keys(updates).length) setProfiles((prev) => ({ ...prev, ...updates }));
    })();
  }, [detail?.messages, profiles]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail?.messages?.length]);

  useEffect(() => {
    useConvArtifactStore.getState().reset();
  }, [conversationId]);

  const markdownComponents = useMemo(
    () => ({
      a({ href, children }: { href?: string; children?: ReactNode }) {
        if (href?.startsWith('#entity-')) {
          const entityId = href.replace('#entity-', '');
          return (
            <button
              type="button"
              className="text-info hover:text-info/80 underline font-medium cursor-pointer bg-transparent border-none p-0 inline"
              onClick={() => loadEntityDetailForConv(entityId)}
            >
              {children}
            </button>
          );
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info hover:underline"
          >
            {children}
          </a>
        );
      },
    }),
    []
  );

  const resolve = useMutation({
    mutationFn: () => resolveConversation(orgId, conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-conversations', orgId] });
      qc.invalidateQueries({ queryKey: ['org-conversation', orgId, conversationId] });
    },
  });
  const archive = useMutation({
    mutationFn: () => archiveConversation(orgId, conversationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-conversations', orgId] });
      qc.invalidateQueries({ queryKey: ['org-conversation', orgId, conversationId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-900 border-t-transparent" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-surface-500">
        Conversation not found.
      </div>
    );
  }

  const showInput = detail.status !== 'RESOLVED' && detail.status !== 'ARCHIVED';

  return (
    <div className="flex-1 flex min-w-0">
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-12 border-b border-surface-200 flex items-center justify-between px-4 bg-surface-0 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="font-semibold text-sm text-surface-900 truncate">{detail.title}</h2>
          <Badge variant={STATUS_VARIANT[detail.status]} className="text-[10.5px]">
            {detail.status.replace(/_/g, ' ').toLowerCase()}
          </Badge>
          {detail.blocksClose && (
            <span className="shrink-0 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warn-surface text-warn">
              Holds month-close
            </span>
          )}
        </div>
        {detail.status !== 'ARCHIVED' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {detail.status !== 'RESOLVED' && (
                <DropdownMenuItem onClick={() => resolve.mutate()}>Mark resolved</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => archive.mutate()}>Archive</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 bg-surface-50">
        <div className="flex flex-col gap-3">
          {(detail.messages ?? []).map((m) => (
            <MessageRow
              key={m.id}
              msg={m}
              profiles={profiles}
              mdComponents={markdownComponents}
            />
          ))}
        </div>
      </div>

      {showInput ? (
        <ReplyInput
          orgId={orgId}
          conversationId={conversationId}
          onSent={() => {
            qc.invalidateQueries({ queryKey: ['org-conversations', orgId] });
            qc.invalidateQueries({ queryKey: ['org-conversation', orgId, conversationId] });
          }}
        />
      ) : (
        <div className="px-5 py-4 border-t border-surface-200 text-xs text-surface-500 italic">
          This conversation is closed.
        </div>
      )}
      </div>
      <ConvArtifactPanel />
    </div>
  );
}
